import React, { useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { GuidService } from 'ui-components/services/guid-service';

import OtherActionsContextProvider from './other-actions-context-provider';
import EditColumnsButtonContainer from '../../../../components/edit-columns-button/containers/orders-grid-container';
import { GridHeaderWithOverflow } from '../../../../components/grid-header/grid-header-with-overflow';
import GroupByButton from '../../../../components/grid-header/group-by-button/index';
import { emptyObject } from 'ui-components/default-props/empty';
import {
  FulfillmentPlanOrderGridStatus,
  acceptableStatusesThatCanBePlacedOnHold,
} from '../../../../enums/fulfillmentplan-order-grid-status';
import { useEnableReship } from '../../../../feature-flags/hooks/use-enable-reship';
import { useEnableOrderPickerRole } from '../../../../hooks/use-get-order-picker-permissions';
import { getOrdersGridAvailableButtons as availableButtons } from '../../../../pages/orders-page/grid-available-buttons';
import Global from '../../../../store/global-selectors';
import { requestGetOrders } from '../../../../store/orders/orders.actions';
import {
  HOLD_BUTTON_ID,
  REMOVE_HOLD_BUTTON_ID,
  RESHIP_BUTTON_ID,
  RESTORE_SHIPMENT_BUTTON_ID,
  READY_TO_PICK_BUTTON_ID,
} from '../../grid-available-buttons/orders-grid-button-ids';
import { useSelectedOrdersView, useSelectedOrdersViewId } from '../../orders-left-nav/orders-left-nav-context';
import { ADVANCED_SEARCH_ID } from '../../orders-page-view-constants';
import OverflowDropdownContainer from '../grid-header-overflow-dropdown-container';

export const OrdersPageGridHeaderWithOverflow = () => {
  const viewId = useSelectedOrdersViewId();
  const isOnAdvancedSearchResults = viewId === ADVANCED_SEARCH_ID;
  const dispatch = useDispatch();
  const onReloadGrid = useCallback(() => {
    dispatch(requestGetOrders());
  }, [dispatch]);
  const isReshipEnabled = useEnableReship();
  const isOrderPickingEnabled = useEnableOrderPickerRole();

  const { label, parentLabel, hideSearchPrefix } = useSelectedOrdersView() || emptyObject;

  const selectedOrderIds = useSelector(Global.selectSelectedOrdersIds);
  const selectedFulfillmentPlans = useSelector((state) =>
    Global.selectEntitiesFulfillmentPlansByIds(state, {
      fulfillmentPlanIds: selectedOrderIds,
    }),
  );
  const hasAdminRole = useSelector(Global.selectUserhasAdministrationRole);
  const hasOrderManagementRole = useSelector(Global.selectUserhasOrderManagementRole);

  const shouldShowHoldButton =
    !!selectedFulfillmentPlans.length &&
    selectedFulfillmentPlans.every((fp) => !!acceptableStatusesThatCanBePlacedOnHold[fp.orderGridStatus]);
  const selectedOrdersAreOnHold = selectedFulfillmentPlans.every((fp) => fp.orderGridStatus === FulfillmentPlanOrderGridStatus.OnHold.name);
  const shouldShowRemoveHoldButton = !!selectedFulfillmentPlans.length && selectedOrdersAreOnHold;
  const shouldShowReadyToPickButton = isOrderPickingEnabled && (hasAdminRole || hasOrderManagementRole);
  const match = GuidService.getGuidRegexMatch(viewId);

  const view = useMemo(() => {
    const mapViewId = match ? viewId.substring(0, match.index - 1) : viewId;
    const buttonObjects = availableButtons()[mapViewId] || [];

    const filteredButtonObjects = buttonObjects.filter((bo) => {
      if (bo.id === HOLD_BUTTON_ID) {
        return shouldShowHoldButton;
      }
      if (bo.id === REMOVE_HOLD_BUTTON_ID) {
        return shouldShowRemoveHoldButton;
      }
      if (bo.id === RESTORE_SHIPMENT_BUTTON_ID) {
        return !isReshipEnabled;
      }
      if (bo.id === RESHIP_BUTTON_ID) {
        return isReshipEnabled;
      }
      if (bo.id === READY_TO_PICK_BUTTON_ID) {
        return shouldShowReadyToPickButton;
      }
      return true;
    });

    const buttons = filteredButtonObjects.map((v, i) => {
      return (
        <v.type key={i} id={v.id} overflowName={v.overflowName}>
          {v.menu ? <v.menu /> : null}
        </v.type>
      );
    });

    const requiredRenderIndices = filteredButtonObjects.reduce((acc, { requiredRender }, index) => {
      if (requiredRender) {
        acc.push(index);
      }
      return acc;
    }, []);

    return {
      id: viewId,
      parentLabel,
      hideSearchPrefix,
      buttons,
      requiredRenderIndices,
      label,
    };
  }, [
    match,
    viewId,
    parentLabel,
    hideSearchPrefix,
    label,
    shouldShowHoldButton,
    shouldShowRemoveHoldButton,
    isReshipEnabled,
    shouldShowReadyToPickButton,
  ]);

  return (
    <OtherActionsContextProvider>
      <GridHeaderWithOverflow
        customOverflowDropdown={OverflowDropdownContainer}
        isQuickshipCompatible
        onReloadGrid={onReloadGrid}
        showReloadButton={!isOnAdvancedSearchResults}
        view={view}
      >
        <GroupByButton />
        <EditColumnsButtonContainer />
      </GridHeaderWithOverflow>
    </OtherActionsContextProvider>
  );
};
